import { TFunc, useGlobalization } from '@Translations';
import React from 'react';
import { ExpandMore } from './AdvancedOptions.styles';
import {
	Collapse,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type DeltaTime = { label: string; value?: number }[];

export const DAYS_1_SEC = 3600 * 24;
export const DEFAULT_QUERY_SIZE_LIMIT = DAYS_1_SEC * 7;

const querySizeLimits = (t: TFunc): DeltaTime => [
	{ label: t('7 Days'), value: DEFAULT_QUERY_SIZE_LIMIT },
	{ label: t('30 Days'), value: DAYS_1_SEC * 30 },
	{ label: t('60 Days'), value: DAYS_1_SEC * 60 },
	{ label: t('90 Days'), value: DAYS_1_SEC * 90 },
	{ label: t('180 Days'), value: DAYS_1_SEC * 180 },
	{ label: t('360 Days'), value: DAYS_1_SEC * 360 },
];

interface AdvancedOptionsProps {
	deltaTimeSeconds?: number;
	onDeltaTimeSecondsChange: React.Dispatch<
		React.SetStateAction<number | undefined>
	>;
}

const AdvancedOptions: React.FC<AdvancedOptionsProps> = ({
	deltaTimeSeconds,
	onDeltaTimeSecondsChange,
}) => {
	const { t } = useGlobalization();
	const title = t('Advanced Options');
	const [expanded, setExpanded] = React.useState(false);

	const handleExpandClick = () => {
		setExpanded(!expanded);
	};

	const onSelectChange = (event: SelectChangeEvent<number>) => {
		onDeltaTimeSecondsChange(event.target.value as number);
	};

	return (
		<div>
			{title}
			<ExpandMore
				title={title}
				color="primary"
				expand={expanded}
				aria-label="show more"
				aria-expanded={expanded}
				onClick={handleExpandClick}
				data-testid="advancedOptionsExpandMoreBtn"
				data-cy="advancedOptionsExpandMoreBtn">
				<ExpandMoreIcon />
			</ExpandMore>
			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						id="querySizeLimitLabel"
						data-testid="querySizeLimitLabel">
						{t('Query Size Limit')}
					</InputLabel>
					<Select
						labelId="querySizeLimitLabel"
						name="querySizeLimit"
						value={deltaTimeSeconds}
						onChange={onSelectChange}
						label={t('Query Size Limit')}
						data-testid="querySizeLimitSelect"
						data-cy="querySizeLimitSelect">
						{querySizeLimits(t).map(({ label, value }) => (
							<MenuItem
								key={label}
								value={value}
								data-testid="querySizeLimitMenuItem"
								data-cy="querySizeLimitMenuItem">
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Collapse>
		</div>
	);
};

export default AdvancedOptions;
